
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const CHAT_API = '/api/ai-chat';


const SparkleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L13.09 8.26L19 7L14.74 11.5L19 16L13.09 14.74L12 21L10.91 14.74L5 16L9.26 11.5L5 7L10.91 8.26L12 2Z"
      fill="rgba(40,227,218,0.9)" stroke="rgba(40,227,218,0.6)" strokeWidth="0.5" />
  </svg>
);
const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18" stroke="#D5D2D2" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 6L18 18" stroke="#D5D2D2" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const LoadingDots = () => (
  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '4px 0' }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{
        width: '7px', height: '7px', borderRadius: '50%',
        background: 'rgba(40,227,218,0.7)',
        animation: 'ujDotPulse 1.2s ease-in-out infinite',
        animationDelay: `${i * 0.2}s`,
      }} />
    ))}
  </div>
);


function escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function inlineFormat(text) {

  const parts = [];
  const re = /\*\*(.*?)\*\*|\*(.*?)\*|\[(.*?)\]\((https?:\/\/[^)]+)\)|`([^`]+)`/g;
  let last = 0, m, key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    if (m[1] !== undefined) parts.push(<strong key={key++} style={{ color: '#fff' }}>{m[1]}</strong>);
    else if (m[2] !== undefined) parts.push(<em key={key++}>{m[2]}</em>);
    else if (m[3] !== undefined) parts.push(
      <a key={key++} href={m[4]} target="_blank" rel="noopener noreferrer"
        style={{ color: 'rgba(40,227,218,0.9)', textDecoration: 'underline' }}>{m[3]}</a>
    );
    else if (m[5] !== undefined) parts.push(
      <code key={key++} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '4px', padding: '1px 4px', fontSize: '12px' }}>{m[5]}</code>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
  return parts;
}

function isSeparator(line) {
  return /^\|?\s*[-:]+[-| :]*\s*\|?\s*$/.test(line);
}

function parseTable(lines) {
  const rows = lines.filter(l => !isSeparator(l));
  const cells = rows.map(r =>
    r.replace(/^\||\|$/g, '').split('|').map(c => c.trim())
  );
  if (cells.length === 0) return null;
  return (
    <div key={Math.random()} style={{ overflowX: 'auto', margin: '8px 0' }}>
      <table style={{
        borderCollapse: 'collapse', width: '100%', fontSize: '12.5px',
        fontFamily: 'Alexandria, sans-serif',
      }}>
        <thead>
          <tr>{cells[0].map((c, i) => (
            <th key={i} style={{
              padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap',
              background: 'rgba(40,227,218,0.15)', border: '1px solid rgba(40,227,218,0.2)',
              color: 'rgba(40,227,218,0.95)', fontWeight: 600,
            }}>{inlineFormat(c)}</th>
          ))}</tr>
        </thead>
        <tbody>
          {cells.slice(1).map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent' }}>
              {row.map((c, ci) => (
                <td key={ci} style={{
                  padding: '5px 10px', textAlign: 'right',
                  border: '1px solid rgba(255,255,255,0.07)', color: '#E8F0EE',
                  verticalAlign: 'top',
                }}>{inlineFormat(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MdContent({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  const output = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];


    if (line.trimStart().startsWith('|')) {
      const block = [];
      while (i < lines.length && (lines[i].trimStart().startsWith('|') || isSeparator(lines[i]))) {
        block.push(lines[i]);
        i++;
      }
      const tbl = parseTable(block);
      if (tbl) output.push(tbl);
      continue;
    }


    if (/^#{1,3}\s/.test(line)) {
      const lvl = line.match(/^(#{1,3})\s/)[1].length;
      const txt = line.replace(/^#{1,3}\s/, '');
      const Tag = `h${lvl + 2}`;
      output.push(
        <Tag key={i} style={{ margin: '10px 0 4px', color: 'rgba(40,227,218,0.9)', fontWeight: 700, fontSize: lvl === 1 ? '15px' : '14px' }}>
          {inlineFormat(txt)}
        </Tag>
      );
      i++; continue;
    }


    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      output.push(
        <ol key={i} style={{ paddingRight: '18px', margin: '6px 0', display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {items.map((t, j) => <li key={j} style={{ color: '#E8F0EE', lineHeight: '1.55' }}>{inlineFormat(t)}</li>)}
        </ol>
      );
      continue;
    }


    if (/^[-*•]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*•]\s/, ''));
        i++;
      }
      output.push(
        <ul key={i} style={{ paddingRight: '18px', margin: '6px 0', display: 'flex', flexDirection: 'column', gap: '3px', listStyle: 'disc' }}>
          {items.map((t, j) => <li key={j} style={{ color: '#E8F0EE', lineHeight: '1.55' }}>{inlineFormat(t)}</li>)}
        </ul>
      );
      continue;
    }


    if (/^[-_*]{3,}$/.test(line.trim())) {
      output.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '8px 0' }} />);
      i++; continue;
    }


    if (line.trim() === '') {
      output.push(<div key={i} style={{ height: '6px' }} />);
      i++; continue;
    }


    output.push(
      <p key={i} style={{ margin: '2px 0', lineHeight: '1.6', color: '#E8F0EE' }}>
        {inlineFormat(line)}
      </p>
    );
    i++;
  }

  return <>{output}</>;
}


const INJECTION_RE = [
  /ignore\s+(all\s+)?(previous|prior|above)\s+instructions?/i,
  /forget\s+(your|all)\s+(instructions?|rules?)/i,
  /you\s+are\s+now\s+(a\s+)?(different|free|unrestricted)/i,
  /act\s+as\s+(a\s+)?(different|unrestricted|free)/i,
  /jailbreak|DAN\b|do\s+anything\s+now/i,
  /system\s*prompt|override\s+the\s+system/i,
  /\[system\]|\[INST\]|<\|system\|>/i,
];
function isInjection(text) {
  return INJECTION_RE.some((r) => r.test(text));
}


let msgIdCounter = 0;
const nextId = () => ++msgIdCounter;

const WELCOME = {
  id: nextId(), role: 'assistant',
  content: 'أهلاً وسهلاً! أنا **همّة AI**، مساعدك المتخصص في شؤون جامعة جدة 🎓\n\nيمكنني مساعدتك في:\n- معلومات الكليات والأقسام\n- القبول والتسجيل والمواعيد\n- الخدمات الإلكترونية\n- اللوائح والأنظمة الأكاديمية\n\nكيف يمكنني مساعدتك اليوم؟',
};


export default function UJChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pulse, setPulse] = useState(true);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => setPulse(false), 6000); return () => clearTimeout(t); }, []);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, messages]);

  const handleOpen = () => { setIsOpen(true); setPulse(false); };

  const handleClose = () => {
    setIsOpen(false);
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setError('');
  };

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    if (isInjection(text)) {
      setInput('');
      setMessages(prev => [
        ...prev,
        { id: nextId(), role: 'user', content: text },
        { id: nextId(), role: 'assistant', content: 'لا يمكنني الاستجابة لهذا النوع من الطلبات. أنا هنا فقط لمساعدتك في شؤون جامعة جدة.' },
      ]);
      return;
    }

    setInput('');
    setError('');
    setMessages(prev => [...prev, { id: nextId(), role: 'user', content: text }]);
    setLoading(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const history = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history }),
        signal: ctrl.signal,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'خطأ في الخادم');
      setMessages(prev => [...prev, { id: nextId(), role: 'assistant', content: data.reply }]);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'حدث خطأ. يرجى المحاولة مجدداً.');
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [input, loading, messages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };


  const chatWindow = (
    <div dir="rtl" style={{
      position: 'fixed',
      bottom: '90px',
      right: '24px',
      width: 'min(400px, calc(100vw - 32px))',
      height: 'min(540px, calc(100vh - 120px))',
      zIndex: 99998,
      display: 'flex', flexDirection: 'column',
      borderRadius: '16px', overflow: 'hidden',
      boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(40,227,218,0.2)',
      background: 'linear-gradient(0deg,rgba(255,255,255,0.02),rgba(255,255,255,0.02)),radial-gradient(92.12% 184.55% at 50% -55%,rgba(40,227,218,0.2) 0%,rgba(40,227,218,0) 100%),#081423',
      fontFamily: 'Alexandria, sans-serif',
      animation: 'ujChatSlideIn 0.22s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      {            }
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '13px 16px', flexShrink: 0,
        borderBottom: '1px solid rgba(40,227,218,0.18)',
        background: 'rgba(40,227,218,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
            background: 'radial-gradient(circle at 40% 30%,rgba(40,227,218,0.35),rgba(8,20,35,0.9))',
            border: '1px solid rgba(40,227,218,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SparkleIcon />
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>همّة AI</div>
            <div style={{ color: 'rgba(40,227,218,0.8)', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#19B228', display: 'inline-block' }} />
              مساعد جامعة جدة الرسمي
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => { setMessages([WELCOME]); setError(''); setInput(''); }}
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(40,227,218,0.2)', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', color: '#D5D2D2', fontSize: '11px', fontFamily: 'Alexandria,sans-serif' }}>
            جديد
          </button>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', borderRadius: '6px' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {              }
      <div style={{
        flex: 1, overflowY: 'auto', padding: '14px 14px 8px',
        display: 'flex', flexDirection: 'column', gap: '12px',
        scrollbarWidth: 'thin', scrollbarColor: 'rgba(40,227,218,0.2) transparent',
      }}>
        {messages.map((msg) => (
          <div key={msg.id} style={{
            display: 'flex',
            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
            alignItems: 'flex-start', gap: '8px',
          }}>
            {msg.role === 'assistant' && (
              <div style={{
                width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
                background: 'radial-gradient(circle,rgba(40,227,218,0.3),rgba(8,20,35,0.9))',
                border: '1px solid rgba(40,227,218,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L13.09 8.26L19 7L14.74 11.5L19 16L13.09 14.74L12 21L10.91 14.74L5 16L9.26 11.5L5 7L10.91 8.26L12 2Z" fill="rgba(40,227,218,0.85)" />
                </svg>
              </div>
            )}
            <div style={{
              maxWidth: '82%',
              padding: msg.role === 'user' ? '9px 13px' : '10px 14px',
              borderRadius: msg.role === 'user' ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
              background: msg.role === 'user'
                ? 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,0.45) 0%,rgba(40,227,218,0) 100%),rgba(40,227,218,0.2)'
                : 'rgba(255,255,255,0.05)',
              border: '1px solid ' + (msg.role === 'user' ? 'rgba(40,227,218,0.3)' : 'rgba(255,255,255,0.08)'),
              fontSize: '13.5px', lineHeight: '1.6', fontFamily: 'Alexandria,sans-serif',
            }}>
              {msg.role === 'assistant'
                ? <MdContent text={msg.content} />
                : <span style={{ color: '#fff', whiteSpace: 'pre-wrap' }}>{msg.content}</span>
              }
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{
              width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
              background: 'radial-gradient(circle,rgba(40,227,218,0.3),rgba(8,20,35,0.9))',
              border: '1px solid rgba(40,227,218,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.09 8.26L19 7L14.74 11.5L19 16L13.09 14.74L12 21L10.91 14.74L5 16L9.26 11.5L5 7L10.91 8.26L12 2Z" fill="rgba(40,227,218,0.85)" />
              </svg>
            </div>
            <div style={{ padding: '10px 14px', borderRadius: '4px 14px 14px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <LoadingDots />
            </div>
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '10px', padding: '8px 12px', color: '#fca5a5',
            fontSize: '12.5px', textAlign: 'center', fontFamily: 'Alexandria,sans-serif',
          }}>{error}</div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {           }
      <div style={{ padding: '10px 14px 12px', borderTop: '1px solid rgba(40,227,218,0.15)', background: 'rgba(8,20,35,0.85)', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: '8px',
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(40,227,218,0.25)',
          borderRadius: '12px', padding: '8px 12px', transition: 'border-color 0.2s',
        }}
          onFocusCapture={e => e.currentTarget.style.borderColor = 'rgba(40,227,218,0.55)'}
          onBlurCapture={e => e.currentTarget.style.borderColor = 'rgba(40,227,218,0.25)'}>
          <textarea
            ref={inputRef} value={input} rows={1} maxLength={700} disabled={loading}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder="اسألني عن جامعة جدة..."
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: '#fff', fontSize: '13.5px', fontFamily: 'Alexandria,sans-serif',
              resize: 'none', overflowY: 'hidden', lineHeight: '1.5',
              direction: 'rtl', textAlign: 'right', minHeight: '22px', maxHeight: '96px',
            }}
          />
          <button onClick={sendMessage} disabled={!input.trim() || loading}
            style={{
              width: '34px', height: '34px', borderRadius: '9px', flexShrink: 0,
              background: input.trim() && !loading
                ? 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,0.5) 0%,rgba(40,227,218,0) 100%),rgba(40,227,218,0.25)'
                : 'rgba(255,255,255,0.06)',
              border: '1px solid ' + (input.trim() && !loading ? 'rgba(40,227,218,0.4)' : 'rgba(255,255,255,0.1)'),
              cursor: input.trim() && !loading ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}>
            <SendIcon />
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '5px', color: 'rgba(255,255,255,0.22)', fontSize: '10.5px', fontFamily: 'Alexandria,sans-serif' }}>
          همّة AI • مخصص لجامعة جدة فقط
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      <style>{`
        @keyframes ujDotPulse { 0%,80%,100%{transform:scale(0.7);opacity:0.5} 40%{transform:scale(1);opacity:1} }
        @keyframes ujChatSlideIn { from{opacity:0;transform:translateY(16px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes ujButtonPulse { 0%{box-shadow:0 0 0 0 rgba(40,227,218,0.45)} 70%{box-shadow:0 0 0 12px rgba(40,227,218,0)} 100%{box-shadow:0 0 0 0 rgba(40,227,218,0)} }
        .uj-chatbot-btn:hover { transform:scale(1.08) !important }
        .uj-chatbot-btn:active { transform:scale(0.96) !important }
        textarea::placeholder { color:rgba(255,255,255,0.38) }
      `}</style>

      {                                   }
   {/*   <button className="uj-chatbot-btn" onClick={isOpen ? handleClose : handleOpen}
        title="همّة AI — مساعد جامعة جدة"
        aria-label="فتح المساعد الذكي لجامعة جدة"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '54px', height: '54px', borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 30%,rgba(40,227,218,0.4) 0%,rgba(8,20,35,0.95) 100%)',
          border: '1.5px solid rgba(40,227,218,0.5)',
          cursor: 'pointer', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          animation: pulse ? 'ujButtonPulse 2s ease-out 1s 2' : 'none',
        }}>
        {isOpen ? <CloseIcon /> : <SparkleIcon />}
      </button>
*/}
      {isOpen && chatWindow}
    </>,
    document.body
  );
}
